import { Controller } from "@hotwired/stimulus";

const MEAL_LOG_WORKSPACE_CONTEXT = "meal_log_workspace";

export default class extends Controller {
  static targets = [ "status", "emptyState", "selectedState", "selectedTitle", "picker", "cancelPicker" ];
  static values = { workspaceUrl: String };

  connect() {
    this.boundRecipeCreated = this.recipeCreated.bind(this);
    document.addEventListener("preparation-recipe-workspace:created", this.boundRecipeCreated);
  }

  disconnect() {
    document.removeEventListener("preparation-recipe-workspace:created", this.boundRecipeCreated);
  }

  openSavedRecipePicker(event) {
    event?.preventDefault();
    this.pickerReturnState = "empty";
    this.returnRecipeSelection = null;
    this.revealPicker();
  }

  changeSelectedRecipe(event) {
    event?.preventDefault();
    this.pickerReturnState = "selected";
    this.returnRecipeSelection = this.currentRecipeSelection();
    this.revealPicker();
  }

  revealPicker() {
    const searchField = document.getElementById("meal_log_entry_recipe_search");
    const autocompleteController = this.autocompleteController();

    this.emptyStateTarget.classList.add("hidden");
    this.selectedStateTarget.classList.add("hidden");
    this.selectedStateTarget.setAttribute("aria-hidden", "true");
    this.pickerTarget.classList.remove("hidden");
    this.pickerTarget.setAttribute("aria-hidden", "false");
    this.cancelPickerTarget.classList.remove("hidden");

    if (searchField) searchField.value = "";
    autocompleteController?.hideSuggestions();
    autocompleteController?.hideClearButton();
    window.requestAnimationFrame(() => searchField?.focus());
  }

  cancelSavedRecipePicker(event) {
    event?.preventDefault();

    const searchField = document.getElementById("meal_log_entry_recipe_search");
    const autocompleteController = this.autocompleteController();
    autocompleteController?.hideSuggestions();

    if (this.pickerReturnState === "selected" && this.returnRecipeSelection?.id) {
      const { label } = this.returnRecipeSelection;
      if (searchField) searchField.value = label;
      autocompleteController.element.dataset.recordAutocompleteSelectedLabel = label;
      autocompleteController.showClearButton();
      this.showSelectedState(label);
      window.requestAnimationFrame(() => this.selectedStateTarget.querySelector("button")?.focus());
      return;
    }

    autocompleteController?.clearValue({ dispatch: false });
    if (searchField) searchField.value = "";
    if (autocompleteController) autocompleteController.element.dataset.recordAutocompleteSelectedLabel = "";
    this.showEmptyState();
    window.requestAnimationFrame(() => this.emptyStateTarget.querySelector("button")?.focus());
  }

  syncRecipeSelection(event) {
    if (event.target?.id !== "meal_log_entry_recipe_id") return;

    if (event.target.value) {
      const searchField = document.getElementById("meal_log_entry_recipe_search");
      const shell = searchField?.closest("[data-controller~='record-autocomplete']");
      const label = searchField?.value || shell?.dataset.recordAutocompleteSelectedLabel || "";
      this.showSelectedState(label);
    } else if (this.pickerTarget.classList.contains("hidden")) {
      this.showEmptyState();
    } else {
      this.selectedStateTarget.classList.add("hidden");
      this.selectedStateTarget.setAttribute("aria-hidden", "true");
      this.cancelPickerTarget.classList.remove("hidden");
    }
  }

  showSelectedState(label) {
    this.selectedTitleTarget.textContent = label;
    this.selectedStateTarget.classList.remove("hidden");
    this.selectedStateTarget.setAttribute("aria-hidden", "false");
    this.emptyStateTarget.classList.add("hidden");
    this.pickerTarget.classList.add("hidden");
    this.pickerTarget.setAttribute("aria-hidden", "true");
    this.cancelPickerTarget.classList.add("hidden");
    this.pickerReturnState = null;
    this.returnRecipeSelection = null;
  }

  showEmptyState() {
    this.selectedTitleTarget.textContent = "";
    this.selectedStateTarget.classList.add("hidden");
    this.selectedStateTarget.setAttribute("aria-hidden", "true");
    this.pickerTarget.classList.add("hidden");
    this.pickerTarget.setAttribute("aria-hidden", "true");
    this.emptyStateTarget.classList.remove("hidden");
    this.cancelPickerTarget.classList.add("hidden");
    this.pickerReturnState = null;
    this.returnRecipeSelection = null;
  }

  removeSelectedRecipe(event) {
    event?.preventDefault();
    const hiddenField = document.getElementById("meal_log_entry_recipe_id");
    const searchField = document.getElementById("meal_log_entry_recipe_search");
    const autocompleteController = this.autocompleteController();

    autocompleteController?.clearValue({ dispatch: false });
    if (searchField) searchField.value = "";
    if (autocompleteController) autocompleteController.element.dataset.recordAutocompleteSelectedLabel = "";
    autocompleteController?.hideSuggestions();
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = "";
      this.statusTarget.classList.add("hidden");
    }
    hiddenField?.dispatchEvent(new Event("change", { bubbles: true }));
    window.requestAnimationFrame(() => this.emptyStateTarget.querySelector("button")?.focus());
  }

  commitRecipeSelection(event) {
    const recipeId = String(event.detail?.id || "").trim();
    const recipeLabel = String(event.detail?.label || "").trim();
    if (!recipeId || !recipeLabel) return;

    this.selectRecipe(recipeId, recipeLabel);
  }

  currentRecipeSelection() {
    const hiddenField = document.getElementById("meal_log_entry_recipe_id");
    const searchField = document.getElementById("meal_log_entry_recipe_search");
    const shell = searchField?.closest("[data-controller~='record-autocomplete']");
    return {
      id: hiddenField?.value || "",
      label: shell?.dataset.recordAutocompleteSelectedLabel || searchField?.value || ""
    };
  }

  autocompleteController() {
    const searchField = document.getElementById("meal_log_entry_recipe_search");
    const shell = searchField?.closest("[data-controller~='record-autocomplete']");
    return shell && this.application.getControllerForElementAndIdentifier(shell, "record-autocomplete");
  }

  openCreationWorkspace(event) {
    event?.preventDefault();
    if (!this.hasWorkspaceUrlValue) return;

    document.dispatchEvent(new CustomEvent("preparation-recipe-workspace:open", {
      detail: {
        trigger: event?.currentTarget || null,
        url: this.workspaceUrlValue,
        context: MEAL_LOG_WORKSPACE_CONTEXT
      }
    }));
  }

  recipeCreated(event) {
    const detail = event.detail || {};
    if (detail.context !== MEAL_LOG_WORKSPACE_CONTEXT) return;

    const recipeId = String(detail.recipeId || "").trim();
    const recipeLabel = String(detail.recipeLabel || "").trim();
    if (!recipeId) return;

    this.selectRecipe(recipeId, recipeLabel);
    this.seedBlankMealFields(recipeLabel, detail);
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = "Saved recipe linked. You can continue with this entry.";
      this.statusTarget.classList.remove("hidden");
    }
  }

  selectRecipe(recipeId, recipeLabel) {
    const hiddenField = document.getElementById("meal_log_entry_recipe_id");
    const searchField = document.getElementById("meal_log_entry_recipe_search");
    if (!hiddenField || !searchField) return;

    hiddenField.value = recipeId;
    searchField.value = recipeLabel;
    const shell = searchField.closest("[data-controller~='record-autocomplete']");
    if (shell) {
      shell.dataset.recordAutocompleteSelectedLabel = recipeLabel;
      shell.querySelector("[data-record-autocomplete-target~='clearButton']")?.classList.remove("hidden");
    }
    hiddenField.dispatchEvent(new Event("change", { bubbles: true }));
  }

  seedBlankMealFields(recipeLabel, detail) {
    const titleField = document.getElementById("meal_log_entry_title");
    if (titleField && !titleField.value.trim() && recipeLabel) {
      titleField.value = recipeLabel;
      titleField.dispatchEvent(new Event("input", { bubbles: true }));
    }

    const dishId = String(detail.recipeDishId || "").trim();
    const dishLabel = String(detail.recipeDishLabel || "").trim();
    const dishField = document.getElementById("meal_log_entry_dish_id");
    if (!dishId || !dishLabel || dishField?.value) return;

    const dishSection = this.element.querySelector("[data-controller~='dish-holiday-form']");
    const dishController = dishSection && this.application.getControllerForElementAndIdentifier(
      dishSection,
      "dish-holiday-form"
    );
    dishController?.chooseExistingDish(dishId, dishLabel, { preservePreparationName: true });
  }
}
